import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaCarSide, FaVanShuttle, FaBusSimple, FaBus } from "react-icons/fa6";
import {
  FiMapPin,
  FiCalendar,
  FiClock,
  FiUser,
  FiCreditCard,
  FiMap,
} from "react-icons/fi";
import { IoCalendarOutline, IoClose } from "react-icons/io5";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import TourSelection from "../components/TourSelection/TourSelection";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import TourForm from "../components/TourForm/TourForm";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";

const TransferForm = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehicleType, setVehicleType] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [formData, setFormData] = useState({
    pickUpDate: new Date(),
    dropOffDate: new Date(),
    returnDate: new Date(),
    pickupLocation: "",
    dropoffLocation: "",
    otherPickupLocation: "",
    otherDropoffLocation: "",
    returnLocation: "",
    otherReturnLocation: "",
    pickupHours: "12",
    pickupMinutes: "00",
    dropoffHours: "12",
    dropoffMinutes: "00",
    returnHours: "12",
    returnMinutes: "00",
    name: "",
    surname: "",
    email: "",
    phone: "",
    persons: "",
    luggage: "",
    serviceType: "one-way",
    carType: "",
    additionalInfo: "",
    paymentMethod: "credit-card",
    isReturn: false,
    airportTicketId: "",
    helicopterTransfer: false,
    atDisposal: false,
    disposalHours: "3",
    // Tour booking related fields
    includeTour: false,
    tourId: "",
    tourDate: new Date(),
    tourTime: { hours: "10", minutes: "00" },
    tourPersons: 2,
    tourPrice: 0,
    tourName: "",
  });

  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false,
  });

  const [errors, setErrors] = useState({});
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [returnPriceEstimate, setReturnPriceEstimate] = useState(null);
  const [totalPriceEstimate, setTotalPriceEstimate] = useState(null);
  const [season, setSeason] = useState("");
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [priceToBeDecided, setPriceToBeDecided] = useState(false);

  const [location, setLocation] = useState(null);

  useEffect(() => {
    // Wait for DOM to be ready
    if (activeStep === 2) {
      const phoneInput = document.getElementsByClassName("PhoneInputInput")[0];
      console.log("Phone input element:", phoneInput);
      if (phoneInput) {
        phoneInput.className =
          "PhoneInputInput rounded-lg p-3 text-gray-800 placeholder-gray-100 focus:ring-2 focus:ring-blue-500 outline-none bg-white/70 placeholder-shown:bg-white/30 backdrop-blur-sm";
      }
    }
  }, [activeStep]);

  // Get price estimate when relevant fields change
  useEffect(() => {
    const getPriceEstimate = async () => {
      // Only get price if both locations and vehicle type are set
      if (
        formData.pickupLocation &&
        formData.dropoffLocation &&
        formData.carType
      ) {
        try {
          setLoadingPrice(true);
          const pickup =
            formData.pickupLocation === "other"
              ? "other"
              : formData.pickupLocation;
          const dropoff =
            formData.dropoffLocation === "other"
              ? "other"
              : formData.dropoffLocation;

          // Don't fetch price for custom locations
          if (pickup === "other" || dropoff === "other") {
            setPriceEstimate(null);
            setReturnPriceEstimate(null);
            setTotalPriceEstimate(null);
            setSeason("");
            setPriceToBeDecided(true);
            setLoadingPrice(false);
            return;
          }

          const date = formData.pickUpDate;
          const serviceType = formData.isReturn ? "round-trip" : "one-way";
          const carType = formData.carType || "car";

          // Add return location if this is a round trip
          let returnLocation = null;
          if (formData.isReturn && formData.returnLocation) {
            returnLocation =
              formData.returnLocation === "other"
                ? "other"
                : formData.returnLocation;
          }

          const response = await axios.get(
            `https://yacht-server.create-site.gr/api/contact/transfer-price`,
            {
              params: {
                pickup,
                dropoff,
                returnLocation,
                date,
                serviceType,
                carType,
              },
            }
          );

          console.log("Price response:", response.data);

          if (response.data) {
            // Set season regardless of whether price is decided
            setSeason(response.data.season || "medium");

            // FIXED: Check if price exists and is > 0, regardless of priceToBeDecided flag
            if (response.data.price && response.data.price > 0) {
              // We have a valid price, so use it regardless of priceToBeDecided flag
              setPriceToBeDecided(false);

              // Handle one-way vs round-trip pricing
              if (formData.isReturn && returnLocation) {
                // For round trip we need to calculate separately
                // The server returns total price, now we need to determine one-way part

                // First get one-way price (without the return param)
                const oneWayResponse = await axios.get(
                  `https://yacht-server.create-site.gr/api/contact/transfer-price`,
                  {
                    params: {
                      pickup,
                      dropoff,
                      date,
                      serviceType: "one-way",
                      carType,
                    },
                  }
                );

                if (oneWayResponse.data && oneWayResponse.data.price > 0) {
                  const oneWayPrice = oneWayResponse.data.price;
                  setPriceEstimate(oneWayPrice);
                  setReturnPriceEstimate(response.data.price - oneWayPrice);
                  setTotalPriceEstimate(response.data.price);
                } else {
                  // If one-way lookup fails, just use total price
                  setPriceEstimate(response.data.price);
                  setReturnPriceEstimate(0);
                  setTotalPriceEstimate(response.data.price);
                }
              } else {
                // Simple one-way price
                setPriceEstimate(response.data.price);
                setReturnPriceEstimate(0);
                setTotalPriceEstimate(response.data.price);
              }
            } else {
              // No valid price
              setPriceToBeDecided(true);
              setPriceEstimate(0);
              setReturnPriceEstimate(0);
              setTotalPriceEstimate(0);
            }
          } else {
            setPriceEstimate(null);
            setReturnPriceEstimate(null);
            setTotalPriceEstimate(null);
            setSeason("");
            setPriceToBeDecided(false);
          }
        } catch (error) {
          console.error("Error fetching price estimate:", error);
          setPriceEstimate(null);
          setReturnPriceEstimate(null);
          setTotalPriceEstimate(null);
          setSeason("");
          setPriceToBeDecided(false);
        } finally {
          setLoadingPrice(false);
        }
      } else {
        // Reset price if data is incomplete
        setPriceEstimate(null);
        setReturnPriceEstimate(null);
        setTotalPriceEstimate(null);
        setSeason("");
        setPriceToBeDecided(false);
      }
    };

    getPriceEstimate();
  }, [
    formData.pickupLocation,
    formData.dropoffLocation,
    formData.returnLocation,
    formData.pickUpDate,
    formData.carType,
    formData.isReturn,
  ]);

  useEffect(() => {
    // Get user's location if on mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      // Check if we have cached location data first
      const cachedLocationData = localStorage.getItem("userLocationData");
      let cachedLocation = null;

      if (cachedLocationData) {
        try {
          const parsedData = JSON.parse(cachedLocationData);
          // Check if the cached data is less than 1 hour old
          const cacheTime = new Date(parsedData.timestamp);
          const currentTime = new Date();
          const cacheAgeHours = (currentTime - cacheTime) / (1000 * 60 * 60);

          if (cacheAgeHours < 1) {
            // Use the cached location if it's recent
            cachedLocation = parsedData;
            console.log("Using cached location data");

            // Set the location data from cache
            setLocation({
              lat: parsedData.lat,
              lng: parsedData.lng,
            });

            setFormData((prev) => ({
              ...prev,
              pickupLocation: "other",
              otherPickupLocation: parsedData.address,
            }));

            // No need to show the loading indicator since we're using cached data
            return;
          }
        } catch (e) {
          console.error("Error parsing cached location data:", e);
          // Continue with getting new location if cache parsing fails
        }
      }

      setLoadingLocation(true);

      const timeoutId = setTimeout(() => {
        if (loadingLocation) {
          setLoadingLocation(false);
          console.log("Location request timed out");
          setFormData((prev) => ({
            ...prev,
            pickupLocation: "other",
            otherPickupLocation: "My current location",
          }));
        }
      }, 50000);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            setLocation(coords);

            // Set coordinates immediately while we try to get the address
            setFormData((prev) => ({
              ...prev,
              pickupLocation: "other",
              otherPickupLocation: `${position.coords.latitude.toFixed(
                5
              )}, ${position.coords.longitude.toFixed(5)}`,
            }));

            // Attempt to get location name using reverse geocoding
            fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${position.coords.latitude},${position.coords.longitude}&key=AIzaSyDMynCGNny6phPgprUn1v44DVlRxmSxI1o`
            )
              .then((response) => {
                if (!response.ok) {
                  throw new Error("Network response was not ok");
                }
                return response.json();
              })
              .then((data) => {
                if (data.status === "OK" && data.results.length > 0) {
                  const address = data.results[0].formatted_address;

                  // Cache the location data with timestamp
                  const locationData = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    address: address,
                    timestamp: new Date().toISOString(),
                  };

                  localStorage.setItem(
                    "userLocationData",
                    JSON.stringify(locationData)
                  );

                  setFormData((prev) => ({
                    ...prev,
                    pickupLocation: "other",
                    otherPickupLocation: address,
                  }));
                }
                // Always turn off loading state
                setLoadingLocation(false);
              })
              .catch((error) => {
                console.error("Error fetching address:", error);
                // Keep the coordinates as the location if geocoding fails
                setLoadingLocation(false);
              });
          },
          (error) => {
            clearTimeout(timeoutId);
            console.error("Geolocation error:", error);
            setLoadingLocation(false);
          },
          { enableHighAccuracy: true, timeout: 50000, maximumAge: 0 }
        );
      } else {
        // Browser doesn't support geolocation
        clearTimeout(timeoutId);
        setLoadingLocation(false);
      }

      // Cleanup function
      return () => clearTimeout(timeoutId);
    }
  }, []);

  const locationOptions = [
    { value: "Agios Ioannis", label: "Agios Ioannis", icon: "🏖️" },
    { value: "Agios lazaros", label: "Agios Lazaros", icon: "🏖️" },
    { value: "Airport", label: "Airport", icon: "✈️" },
    { value: "jackie o", label: "Jackie O", icon: "🍸" },
    { value: "kalafatis", label: "Kalafatis", icon: "🏖️" },
    { value: "kalo livadi", label: "Kalo Livadi", icon: "🏖️" },
    { value: "lia", label: "Lia", icon: "🏖️" },
    { value: "ornos", label: "Ornos", icon: "🏖️" },
    { value: "panormos", label: "Panormos", icon: "🏖️" },
    { value: "port", label: "Port", icon: "🚢" },
    { value: "town", label: "Town", icon: "🏙️" },
    { value: "agios sostis", label: "Agios Sostis", icon: "🏖️" },
    { value: "agrari", label: "Agrari", icon: "🏖️" },
    { value: "alemagou", label: "Alemagou", icon: "🍹" },
    { value: "JosefinSansmandra", label: "JosefinSansmandra", icon: "🏖️" },
    { value: "ano mera", label: "Ano Mera", icon: "🏞️" },
    { value: "chalara", label: "Chalara", icon: "🏝️" },
    { value: "choulakia", label: "Choulakia", icon: "🏖️" },
    { value: "elia", label: "Elia", icon: "🏖️" },
    { value: "fabrika", label: "Fabrika", icon: "🏙️" },
    { value: "fanari", label: "Fanari", icon: "🏔️" },
    { value: "fokos", label: "Fokos", icon: "🌊" },
    { value: "ftelia", label: "Ftelia", icon: "🏖️" },
    { value: "kanalia", label: "Kanalia", icon: "🏘️" },
    { value: "other", label: "Other Location", icon: "📍" },
    { value: "paradise", label: "Paradise", icon: "🌴" },
    { value: "paraga", label: "Paraga", icon: "🏖️" },
    { value: "platis gialos", label: "Platis Gialos", icon: "🏖️" },
    { value: "pouli", label: "Pouli", icon: "🏠" },
    { value: "psarrou", label: "Psarrou", icon: "🛥️" },
    { value: "santanna", label: "SantAnna", icon: "🎶" },
    { value: "scorpios", label: "Scorpios", icon: "🌅" },
    { value: "spilia", label: "Spilia", icon: "🪨" },
    { value: "super paradise", label: "Super Paradise", icon: "🌞" },
    { value: "tropicana", label: "Tropicana", icon: "🌺" },
    { value: "vrisi", label: "Vrisi", icon: "💧" },
    { value: "other", label: "Other Location", icon: "📍" },
  ];

  const carTypeOptions = [
    {
      value: "car",
      label: "Car",
      icon: FaCarSide,
      description: "Luxury sedan for 1-3 passengers",
      maxPassengers: 3,
      maxLuggage: 3,
    },
    {
      value: "minivan",
      label: "Mini Van",
      icon: FaVanShuttle,
      description: "Spacious minivan for 4-7 passengers",
      maxPassengers: 7,
      maxLuggage: 7,
    },
    {
      value: "van",
      label: "Big Van",
      icon: FaBusSimple,
      description: "Large van for 8-12 passengers",
      maxPassengers: 12,
      maxLuggage: 12,
    },
    {
      value: "minibus",
      label: "Mini Bus",
      icon: FaBus,
      description: "Mini bus for 13-20 passengers",
      maxPassengers: 20,
      maxLuggage: 20,
    },
  ];

  const disposalHoursOptions = [
    { value: "3", label: "3 Hours" },
    { value: "6", label: "6 Hours" },
    { value: "12", label: "12 Hours" },
    { value: "24", label: "24 Hours" },
    { value: "custom", label: "Custom" },
  ];

  const validateCapacity = () => {
    const currentVehicle = carTypeOptions.find(
      (opt) => opt.value === vehicleType
    );
    const newErrors = {};

    if (!currentVehicle) return newErrors;

    const persons = parseInt(formData.persons);
    const luggage = parseInt(formData.luggage);

    if (persons > currentVehicle.maxPassengers) {
      newErrors.persons = `This vehicle can only accommodate up to ${currentVehicle.maxPassengers} passengers`;
      const recommendedVehicle = carTypeOptions.find(
        (v) => v.maxPassengers >= persons
      );
      if (recommendedVehicle && recommendedVehicle.value !== vehicleType) {
        newErrors.personsRecommendation = `Please consider booking a ${recommendedVehicle.label} instead`;
      }
    }

    if (luggage > currentVehicle.maxLuggage) {
      newErrors.luggage = `This vehicle can only accommodate up to ${currentVehicle.maxLuggage} luggage items`;
      const recommendedVehicle = carTypeOptions.find(
        (v) => v.maxLuggage >= luggage
      );
      if (recommendedVehicle && recommendedVehicle.value !== vehicleType) {
        newErrors.luggageRecommendation = `Please consider booking a ${recommendedVehicle.label} instead`;
      }
    }

    return newErrors;
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Handle return toggle
    if (name === "isReturn" && type === "checkbox") {
      if (checked) {
        // Enable return mode, but keep existing dropoff values
        setFormData((prev) => ({
          ...prev,
          isReturn: true,
        }));
      } else {
        // If unchecking return, sync dropoff with pickup
        setFormData((prev) => ({
          ...prev,
          isReturn: false,
          dropOffDate: prev.pickUpDate,
          dropoffHours: prev.pickupHours,
          dropoffMinutes: prev.pickupMinutes,
          dropoffLocation: prev.pickupLocation,
          otherDropoffLocation: prev.otherPickupLocation,
        }));
      }
    }

    // Clear error when field is changed
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle date changes
  const handleDateChange = (date, type) => {
    const isPickup = type === "pickUpDate";
    const stateKey = isPickup ? "pickup" : "dropoff";

    // Check if both date and time are set
    const hasHours = formData[isPickup ? "pickupHours" : "dropoffHours"];
    const hasMinutes = formData[isPickup ? "pickupMinutes" : "dropoffMinutes"];
    const hasTime = hasHours && hasMinutes;

    // Show warning if time is not selected
    setTimeWarnings((prev) => ({
      ...prev,
      [stateKey]: !hasTime,
    }));

    setFormData((prev) => ({
      ...prev,
      [type]: date,
      // If changing pickup date and it's after dropoff, update dropoff date
      ...(isPickup && date > prev.dropOffDate ? { dropOffDate: date } : {}),
    }));
  };
  // Select vehicle type
  const handleVehicleSelect = (type) => {
    setVehicleType(type);
    setFormData((prev) => ({ ...prev, carType: type }));
    // Stays on same step until user clicks continue
  };
  // Handle tour selection
  const handleTourSelect = (tourData) => {
    if (tourData) {
      setFormData({
        ...formData,
        includeTour: true,
        tourId: tourData.tourId,
        tourDate: tourData.tourDate,
        tourTime: tourData.tourTime,
        tourPersons: tourData.tourPersons,
        tourPrice: tourData.tourPrice,
        tourName: tourData.tourName,
      });
    } else {
      setFormData({
        ...formData,
        includeTour: false,
        tourId: "",
        tourDate: new Date(),
        tourTime: { hours: "10", minutes: "00" },
        tourPersons: formData.persons || 2,
        tourPrice: 0,
        tourName: "",
      });
    }
  };

  // Add history handling for mobile back gesture
  useEffect(() => {
    // Function to handle browser's back button or gesture
    const handleBackNavigation = (event) => {
      // Only intercept back navigation if we're past the first step
      if (activeStep > 1) {
        // Prevent the default back behavior
        event.preventDefault();

        // Go back one step in the form instead
        prevStep();

        // Push a new entry to maintain history stack
        window.history.pushState(
          null,
          document.title,
          window.location.pathname
        );

        return;
      }
      // If on first step, allow normal back functionality
    };

    // Add a history entry when component mounts
    window.history.pushState(null, document.title, window.location.pathname);

    // Listen for popstate events (back navigation)
    window.addEventListener("popstate", handleBackNavigation);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
    };
  }, [activeStep]); // Re-establish listener when active step changes

  // Navigate between steps
  const nextStep = () => {
    // Validate fields before proceeding
    const newErrors = {};

    if (activeStep === 1) {
      if (!vehicleType) {
        newErrors.vehicleType = "Please select a vehicle type";
      }
      if (!formData.pickupLocation) {
        newErrors.pickupLocation = "Please select a pickup location";
      }
      if (
        formData.pickupLocation === "other" &&
        !formData.otherPickupLocation
      ) {
        newErrors.otherPickupLocation = "Please enter the pickup location";
      }
      if (!formData.dropoffLocation) {
        newErrors.dropoffLocation = "Please select a drop-off location";
      }
      if (
        formData.dropoffLocation === "other" &&
        !formData.otherDropoffLocation
      ) {
        newErrors.otherDropoffLocation = "Please enter the drop-off location";
      }
      if (formData.isReturn) {
        if (!formData.returnLocation) {
          newErrors.returnLocation = "Please select a return location";
        }
      }
      if (!formData.pickupHours) {
        newErrors.pickupHours = "Please select a pickup hour";
      }
      if (formData.pickUpDate > formData.dropOffDate) {
        newErrors.pickUpDate = "Pickup date cannot be after drop-off date";
      }
      if (formData.dropOffDate < formData.pickUpDate) {
        newErrors.dropOffDate = "Drop-off date cannot be before pickup date";
      }
    } else if (activeStep === 2) {
      if (!formData.name) newErrors.name = "Please enter your first name";
      if (!formData.surname) newErrors.surname = "Please enter your last name";
      if (!formData.email) {
        newErrors.email = "Please enter your email";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.phone) {
        newErrors.phone = "Please enter your phone number";
      }
      //   if phone does not contain area code, e.g. +30, +1, etc.
      if (formData.phone && !/^\+\d{1,3}\d{9,}$/.test(formData.phone)) {
        newErrors.phone = "Please enter a valid phone number with area code";
      }
      if (!formData.persons) {
        newErrors.persons = "Please enter number of passengers";
      }

      // Add capacity validation
      const capacityErrors = validateCapacity();
      Object.assign(newErrors, capacityErrors);
    }

    const capacityErrors = validateCapacity();
    Object.assign(newErrors, capacityErrors);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep((prev) => prev + 1);
      setIsAnimating(false);

      // Push a new history state when advancing to a new step
      window.history.pushState(null, document.title, window.location.pathname);
    }, 300);
  };

  const prevStep = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setActiveStep((prev) => prev - 1);
      setIsAnimating(false);
    }, 300);
  };

  // on page load scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  

  const validateForm = () => {
    const newErrors = {};

    // Validation for step 3
    if (!formData.persons) {
      newErrors.persons = "Please enter number of passengers";
    }
    if (!formData.luggage) {
      newErrors.luggage = "Please enter number of luggage items";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Combine date and time
    const pickupDateTime = new Date(formData.pickUpDate);
    pickupDateTime.setHours(
      parseInt(formData.pickupHours),
      parseInt(formData.pickupMinutes)
    );

    const dropoffDateTime = new Date(formData.dropOffDate);
    dropoffDateTime.setHours(
      parseInt(formData.dropoffHours),
      parseInt(formData.dropoffMinutes)
    );

    try {
      // Prepare the data
      const transferData = {
        ...formData,
        pickUpDate: pickupDateTime,
        dropOffDate: dropoffDateTime,
        pickupLocation:
          formData.pickupLocation === "other"
            ? formData.otherPickupLocation
            : locationOptions.find(
                (opt) => opt.value === formData.pickupLocation
              )?.label,
        dropoffLocation:
          formData.dropoffLocation === "other"
            ? formData.otherDropoffLocation
            : locationOptions.find(
                (opt) => opt.value === formData.dropoffLocation
              )?.label,
      };

      // First submit the transfer request
      let transferResponse;
      try {
        transferResponse = await axios.post(
          "https://yacht-server.create-site.gr/api/contact/transfer",
          transferData
        );
      } catch (error) {
        console.error("Error submitting transfer request:", error);
        throw error;
      }

      // If tour is selected, book the tour and associate it with the transfer
      if (formData.includeTour && formData.tourId) {
        try {
          // First get the most recently created transfer for this user
          const userTransfersResponse = await axios.get(
            `https://yacht-server.create-site.gr/api/contact/transfers?email=${encodeURIComponent(
              formData.email
            )}&recent=true`
          );

          // Check if we got a transfer and extract its ID
          if (
            userTransfersResponse.data &&
            userTransfersResponse.data.length > 0
          ) {
            const transferId = userTransfersResponse.data[0]._id;
            const tourBookingData = {
              transfer: transferId,
              tour: formData.tourId,
              vehicleType: formData.carType || "car",
              tourDate: formData.tourDate,
              tourTime: formData.tourTime,
              numberOfPersons: formData.tourPersons,
              notes: formData.additionalInfo,
            };

            await axios.post(
              "https://yacht-server.create-site.gr/api/tour-bookings",
              tourBookingData
            );
          } else {
            throw new Error("Could not find the transfer ID for tour booking");
          }
        } catch (tourError) {
          console.error("Error booking tour:", tourError);
          Swal.fire({
            title: "Warning",
            text: "Transfer was booked, but we couldn't book your tour. Please contact support.",
            icon: "warning",
            confirmButtonColor: "#1e3a8a",
          });
        }
      }

      Swal.fire({
        title: "Success!",
        text: `Your transfer request has been submitted successfully!`,
        icon: "success",
        confirmButtonColor: "#1e3a8a",
      }).then(() => {
        // Reset form
        setFormData({
          pickUpDate: new Date(),
          dropOffDate: new Date(),
          pickupLocation: "",
          dropoffLocation: "",
          otherPickupLocation: "",
          otherDropoffLocation: "",
          pickupHours: "12",
          pickupMinutes: "00",
          dropoffHours: "12",
          dropoffMinutes: "00",
          name: "",
          surname: "",
          email: "",
          phone: "",
          persons: "",
          luggage: "",
          serviceType: "one-way",
          carType: "",
          additionalInfo: "",
          paymentMethod: "credit-card",
          isReturn: false,
          airportTicketId: "",
          helicopterTransfer: false,
          atDisposal: false,
          disposalHours: "3",
        });
        setActiveStep(1);
        setVehicleType("");
      });
    } catch (error) {
      console.error("Error submitting transfer request:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to submit your request. Please try again.",
        icon: "error",
        confirmButtonColor: "#1e3a8a",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const backgroundImage = require("../assets/images/windmill-in-mykonos-greece-5132.jpg");

  return (
    <>
      <Header />
      <main
        className="flex-grow bg-cover bg-center bg-no-repeat py-12 min-h-screen"
        id="transfer-form"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <TourForm />
      </main>
      <Footer />
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .animate-pulse {
          animation: pulse 1.5s infinite;
        }

        .animate-bounce {
          animation: bounce 0.8s infinite;
        }
      `}</style>
    </>
  );
};

export default TransferForm;
